import { useState } from 'react';
import { Edit, Check } from 'lucide-react';
import ListContainer from '../ListContainer/ListContainer';
import ListElement from '../ListElement/ListElement';
import ElementActions from '../ElementActions/ElementActions';
import IconButton from '../IconButton/IconButton';
import { Icon } from '../Icon/Icon';
import { PrivateResourceLink } from '../PrivateResourceLink/PrivateResourceLink';
import { PublicResourceLink } from '../PublicResourceLink/PublicResourceLink';
import { Dot } from '../Dot/Dot';
import deleteIcon from '../../assets/images/bin.svg';
import fileIcon from '../../assets/images/file.svg';

type ResourceLinkType = 'private' | 'public' | 'custom';

type FileListProps = {
  existingFiles?: string[];
  pendingFiles?: File[];
  onRemoveExisting?: (url: string) => void;
  onRemovePending?: (fileName: string) => void;
  onRenamePending?: (file: File, newName: string, index: number) => void;
  isDisabled?: boolean;
  resourceLinkType?: ResourceLinkType;
  resourceLinkProps?: {
    type?: string;
    prefixId?: string;
    trackingId?: string;
  };
  renderExistingFile?: (url: string, index: number) => JSX.Element;
  renderPendingFile?: (file: File, index: number) => JSX.Element;
};

function FileList({
  existingFiles = [],
  pendingFiles = [],
  onRemoveExisting,
  onRemovePending,
  onRenamePending,
  isDisabled = false,
  resourceLinkType = 'private',
  resourceLinkProps = {},
  renderExistingFile,
  renderPendingFile,
}: FileListProps) {
  const [editingFileIndex, setEditingFileIndex] = useState<number | null>(null);
  const [editingFileName, setEditingFileName] = useState<string>('');
  const [originalFileName, setOriginalFileName] = useState<string>('');
  const renderExistingFileContent = (url: string, index: number): JSX.Element => {
    if (renderExistingFile) {
      return renderExistingFile(url, index);
    }

    let resourceLink: JSX.Element;
    if (resourceLinkType === 'public' && resourceLinkProps.trackingId) {
      resourceLink = <PublicResourceLink file={url} trackingId={resourceLinkProps.trackingId} />;
    } else if (resourceLinkType === 'private') {
      resourceLink = (
        <PrivateResourceLink
          file={url}
          type={resourceLinkProps.type}
          prefixId={resourceLinkProps.prefixId}
        />
      );
    } else {
      // Fallback: just show the filename
      const fileName = url.split('/').pop() || url;
      resourceLink = <span className="font-mono text-xs">{fileName}</span>;
    }

    return (
      <span>
        <Icon url={fileIcon} cssStyles="w-5" /> {resourceLink}
      </span>
    );
  };

  const getFileExtension = (fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
  };

  const handleStartEdit = (file: File, index: number) => {
    setEditingFileIndex(index);
    const nameWithoutExt = file.name.replace(getFileExtension(file.name), '');
    setEditingFileName(nameWithoutExt);
    setOriginalFileName(file.name);
  };

  const handleCancelEdit = () => {
    setEditingFileIndex(null);
    setEditingFileName('');
    setOriginalFileName('');
  };

  const handleSaveEdit = (file: File, index: number) => {
    if (onRenamePending && editingFileName.trim()) {
      const extension = getFileExtension(originalFileName);
      const newName = editingFileName.trim() + extension;
      if (newName !== originalFileName) {
        onRenamePending(file, newName, index);
      }
    }
    handleCancelEdit();
  };

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>, originalName: string) => {
    const newValue = e.target.value;
    // Only allow the name part (without extension) to be edited
    // The extension will be appended automatically
    setEditingFileName(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, file: File, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit(file, index);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const renderPendingFileContent = (file: File, index: number): JSX.Element => {
    if (renderPendingFile) {
      return renderPendingFile(file, index);
    }

    if (editingFileIndex === index) {
      const extension = getFileExtension(originalFileName);
      return (
        <span className="flex items-center space-x-2 flex-1">
          <Dot />
          <div className="flex items-center space-x-1 flex-1">
            <input
              type="text"
              value={editingFileName}
              onChange={(e) => handleFileNameChange(e, originalFileName)}
              onKeyDown={(e) => handleKeyDown(e, file, index)}
              onBlur={handleCancelEdit}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-sm text-gray-500">{extension}</span>
            <button
              type="button"
              onClick={() => handleSaveEdit(file, index)}
              onMouseDown={(e) => e.preventDefault()}
              className="flex items-center hover:text-slate-700 hover:cursor-pointer transition-brightness duration-200"
            >
              <Check className="w-5 h-5 text-green-500" />
            </button>
          </div>
        </span>
      );
    }

    return (
      <span>
        <Dot /> {file.name}
      </span>
    );
  };

  return (
    <>
      {existingFiles.length > 0 && (
        <ListContainer
          elements={existingFiles}
          toElementFn={(url: string, index: number) => (
            <ListElement key={`${url}-${index}` || index}>
              <ElementActions
                actions={
                  isDisabled || !onRemoveExisting ? (
                    <></>
                  ) : (
                    <IconButton onClick={() => onRemoveExisting(url)}>
                      <Icon url={deleteIcon} cssStyles="w-5" />
                    </IconButton>
                  )
                }
              >
                {renderExistingFileContent(url, index)}
              </ElementActions>
            </ListElement>
          )}
        />
      )}
      {pendingFiles.length > 0 && (
        <ListContainer
          elements={pendingFiles}
          toElementFn={(file: File, index: number) => (
            <ListElement key={`${file?.name}-${index}` || index}>
              <ElementActions
                actions={
                  isDisabled ? (
                    <></>
                  ) : (
                    <div className="flex items-center space-x-1">
                      {onRenamePending && editingFileIndex !== index && (
                        <IconButton onClick={() => handleStartEdit(file, index)}>
                          <Edit className="w-5 h-5" />
                        </IconButton>
                      )}
                      {onRemovePending && (
                        <IconButton onClick={() => onRemovePending(file.name)}>
                          <Icon url={deleteIcon} cssStyles="w-5" />
                        </IconButton>
                      )}
                    </div>
                  )
                }
              >
                {renderPendingFileContent(file, index)}
              </ElementActions>
            </ListElement>
          )}
        />
      )}
    </>
  );
}

export default FileList;

